import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import SettingsLayout from "../components/SettingsLayout";
import { apiKeys as styles } from "./ApiKeys.styles";
import apiKeysTokens from "./ApiKeys.tokens";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";
import FeatureKeys from "global/constants/FeatureKeys";
import { useFeatureState } from "hooks/use-feature";
import {
  ApiKeyListItem,
  ISSUED_KEY_STATUS,
  IssuedApiKey,
  fetchApiKeys,
  issueApiKey,
  revokeApiKey,
} from "./ApiKeysService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useAriaLive } from "hooks/use-aria-live";
import { LiveRegion } from "components/live-region";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import EmptyState from "global/components/EmptyState/EmptyState";
import { focusFirstInvalidField } from "utils/accessibility";
import EmbedOrigins from "./EmbedOrigins";

const DATE_FORMAT = "MM/DD/YYYY HH:mm:ss";
const NAME_MAX = 64;
const NAME_FIELD_ID = "api_key_name";
// A <select> is used for the expiry so the server's silent clamp at 1095 can
// never be triggered by a hand-typed value.
const EXPIRY_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "365 days (default)" },
  { value: 1095, label: "1095 days (3 years)" },
];

/** Name / Key ID / Expires / Status / Actions — the Invoices list's grid,
 *  narrowed from seven columns to this screen's five. */
const GRID = apiKeysTokens.list.grid;
const listTokens = apiKeysTokens.list;

const parseServerDate = (value?: string) =>
  value ? moment(value, DATE_FORMAT, true) : null;

const formatExpiry = (value?: string) => {
  const parsed = parseServerDate(value);
  return parsed && parsed.isValid() ? parsed.format("MMM D, YYYY") : "—";
};

const isPastExpiry = (value?: string) => {
  const parsed = parseServerDate(value);
  return !!(parsed && parsed.isValid() && parsed.isBefore(moment()));
};

const ApiKeys = () => {
  useTitle(title.INTEGRATIONS);
  const theme = useTheme();
  const { announcement, priority, announce } = useAriaLive();
  const featureState = useFeatureState(FeatureKeys.API_ACCESS);
  const featureAvailable = featureState.status === "available";

  const [keys, setKeys] = useState<ApiKeyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRevoked, setShowRevoked] = useState(false);

  // Create / reveal modal
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(365);
  const [creating, setCreating] = useState(false);
  const [revealKey, setRevealKey] = useState<IssuedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyListItem | null>(null);
  const [revoking, setRevoking] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchApiKeys();
      setKeys(data);
    } catch (error: any) {
      const message =
        error?.message ?? "Couldn't load your API keys. Please try again.";
      openErrorNotification(message);
      announce(message, "assertive");
    } finally {
      setLoading(false);
    }
  }, [announce]);

  useEffect(() => {
    if (featureAvailable) {
      loadKeys();
    } else {
      setLoading(false);
    }
  }, [featureAvailable, loadKeys]);

  const { activeKeys, revokedKeys } = useMemo(() => {
    const active: ApiKeyListItem[] = [];
    const revoked: ApiKeyListItem[] = [];
    keys.forEach((key) =>
      (key.status === ISSUED_KEY_STATUS.revoked ? revoked : active).push(key),
    );
    return { activeKeys: active, revokedKeys: revoked };
  }, [keys]);

  const resetCreateForm = () => {
    setName("");
    setNameError("");
    setExpiresInDays(365);
    setCopied(false);
  };

  const openCreate = () => {
    resetCreateForm();
    setRevealKey(null);
    setCreateOpen(true);
  };

  // While the one-time key is on screen nothing may dismiss it — there is no way
  // to view it again. The scrim and Esc are held off by the modal itself; this
  // guard covers the close button.
  const handleDialogClose = () => {
    if (revealKey) return;
    setCreateOpen(false);
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Name is required");
      focusFirstInvalidField([NAME_FIELD_ID]);
      return;
    }
    if (trimmed.length > NAME_MAX) {
      setNameError(`Name must be ${NAME_MAX} characters or fewer`);
      focusFirstInvalidField([NAME_FIELD_ID]);
      return;
    }
    try {
      setCreating(true);
      const issued = await issueApiKey({
        name: trimmed,
        expires_in_days: expiresInDays,
      });
      setRevealKey(issued);
    } catch (error: any) {
      const message =
        error?.message ?? "Couldn't create the API key. Please try again.";
      openErrorNotification(message);
      announce(message, "assertive");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!revealKey) return;
    try {
      await navigator.clipboard.writeText(revealKey.full_key);
      setCopied(true);
      announce("API key copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const message =
        "Couldn't copy automatically — select the key and copy it manually.";
      openErrorNotification(message);
      announce(message, "assertive");
    }
  };

  // The only way out of the reveal — the key is gone after this.
  const handleRevealDone = async () => {
    setCreateOpen(false);
    setRevealKey(null);
    resetCreateForm();
    await loadKeys();
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      await revokeApiKey(revokeTarget.keyId);
      openSuccessNotification("API key revoked.");
      announce("API key revoked.");
      setRevokeTarget(null);
      await loadKeys();
    } catch (error: any) {
      const message =
        error?.message ?? "Couldn't revoke the key. Please try again.";
      openErrorNotification(message);
      announce(message, "assertive");
    } finally {
      setRevoking(false);
    }
  };

  /** Status pill — same stadium shape as the Invoices STATUS pill; the tone
   *  is the only thing that changes per state. */
  const renderPill = (key: ApiKeyListItem) => {
    const isRevoked = key.status === ISSUED_KEY_STATUS.revoked;
    const isExpired = !isRevoked && isPastExpiry(key.expiresOn);
    const tone = isRevoked
      ? apiKeysTokens.chip.revoked
      : isExpired
        ? apiKeysTokens.chip.expired
        : apiKeysTokens.chip.active;
    const label = isRevoked ? "Revoked" : isExpired ? "Expired" : "Active";
    return (
      <Box component="span" sx={styles.pillSx(tone)}>
        {label.toUpperCase()}
      </Box>
    );
  };

  /** One key's row — desktop grid, hidden below `md` in favour of `getCardRow`. */
  const getRow = (key: ApiKeyListItem, isLast: boolean) => (
    <Box key={key.id} sx={styles.listRowSx(GRID, isLast)}>
      <Box component="span">
        <Box
          component="span"
          sx={styles.rowTextSx(listTokens.color.ink, listTokens.weight.strong)}
        >
          {key.name}
        </Box>
        <Box component="span" sx={styles.rowMetaLineSx}>
          {key.rateLimitPerMin
            ? `${key.rateLimitPerMin}/min`
            : "60/min (default)"}
        </Box>
      </Box>
      <Box
        component="span"
        sx={styles.rowTextSx(listTokens.color.body, listTokens.weight.regular)}
      >
        {key.keyId}
      </Box>
      <Box
        component="span"
        sx={styles.rowTextSx(listTokens.color.body, listTokens.weight.regular)}
      >
        {formatExpiry(key.expiresOn)}
      </Box>
      <Box component="span">{renderPill(key)}</Box>
      <Box sx={styles.rowActionCellSx}>
        {key.status !== ISSUED_KEY_STATUS.revoked && (
          <Tooltip title="Revoke" arrow>
            <IconButton
              size="small"
              aria-label={`Revoke API key ${key.name}`}
              onClick={() => setRevokeTarget(key)}
              sx={styles.rowRevokeButtonSx}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  /**
   * Below `md` a key is a flat row, not a grid — same split the Invoices list
   * makes at this width. Hidden on desktop in favour of `getRow`.
   */
  const getCardRow = (key: ApiKeyListItem, isLast: boolean) => (
    <Box key={key.id} sx={styles.keyCardRowSx(isLast)}>
      <Box sx={styles.keyCardTextSx}>
        <Box sx={styles.keyCardTopSx}>
          <Box component="span" sx={styles.keyCardNameSx}>
            {key.name}
          </Box>
          {renderPill(key)}
        </Box>
        <Box component="span" sx={styles.keyCardMetaSx}>
          {`${key.keyId} · Expires ${formatExpiry(key.expiresOn)}`}
        </Box>
      </Box>

      {key.status !== ISSUED_KEY_STATUS.revoked && (
        <Tooltip title="Revoke" arrow>
          <IconButton
            size="small"
            aria-label={`Revoke API key ${key.name}`}
            onClick={() => setRevokeTarget(key)}
            sx={styles.keyCardActionSx}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  /** Column-header strip — unconditional, same as the Invoices list's. */
  const renderColumnHeader = () => (
    <Box sx={styles.columnHeaderSx(GRID)}>
      <Box component="span">NAME</Box>
      <Box component="span">KEY ID</Box>
      <Box component="span">EXPIRES</Box>
      <Box component="span">STATUS</Box>
      <Box component="span" sx={{ textAlign: "right" }}>
        ACTIONS
      </Box>
    </Box>
  );

  const renderEmptyState = () => (
    <EmptyState
      icon="key"
      title="No API keys yet"
      body="Issue a key to connect Inkfree to your own tools, or to Zapier. You will see the secret once, at the moment it is created."
      // Nothing to create while the feature is locked; the banner above says why.
      primaryAction={
        featureAvailable
          ? { label: "Create API key", onClick: openCreate, disabled: loading }
          : undefined
      }
    />
  );

  /** Skeleton row, shaped exactly like `getRow` so nothing jumps when the
   *  real data lands. */
  const getLoadingRow = (index: number, isLast: boolean) => (
    <Box
      key={index}
      aria-hidden="true"
      sx={styles.listRowSx(GRID, isLast)}
    >
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton variant="text" width="80%" height={20} />
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="rounded" width={60} height={20} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Skeleton variant="circular" width={28} height={28} />
      </Box>
    </Box>
  );

  // The column-header strip stays up over every state — loading, empty,
  // gated or populated — same as the Invoices list's, which is unconditional
  // and only the rows swap to an empty message. A locked feature is no
  // different: the header says what this list is, and the banner above
  // already explains why there's nothing under it.
  const renderContent = () => (
    <>
      {renderColumnHeader()}

      {loading ? (
        [0, 1, 2].map((i) => getLoadingRow(i, i === 2))
      ) : keys.length === 0 ? (
        renderEmptyState()
      ) : activeKeys.length > 0 ? (
        <>
          {activeKeys.map((key, index) =>
            getRow(key, index === activeKeys.length - 1),
          )}
          {activeKeys.map((key, index) =>
            getCardRow(key, index === activeKeys.length - 1),
          )}
        </>
      ) : (
        /* A sub-list inside an already-populated card: the compact size,
           since the revoked keys sit below it. */
        <EmptyState size="compact" title="No active keys" />
      )}

      {!loading && revokedKeys.length > 0 && (
        <Box sx={{ px: listTokens.space.gutter, py: "12px" }}>
          <Button
            onClick={() => setShowRevoked((prev) => !prev)}
            endIcon={showRevoked ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={showRevoked}
            aria-controls="revoked-keys-panel"
            sx={{
              textTransform: "none",
              color: listTokens.color.muted,
              fontWeight: 600,
            }}
          >
            {showRevoked ? "Hide" : "Show"} revoked keys ({revokedKeys.length})
          </Button>
          <Collapse in={showRevoked} id="revoked-keys-panel">
            <Box
              sx={{
                mt: 1,
                border: `1px solid ${apiKeysTokens.card.border}`,
                borderRadius: apiKeysTokens.card.radius,
                overflow: "hidden",
                opacity: 0.85,
              }}
            >
              {renderColumnHeader()}
              {revokedKeys.map((key, index) =>
                getRow(key, index === revokedKeys.length - 1),
              )}
              {revokedKeys.map((key, index) =>
                getCardRow(key, index === revokedKeys.length - 1),
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {!loading && keys.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{
            px: listTokens.space.gutter,
            py: "14px",
            color: listTokens.color.muted,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, mt: "2px", flexShrink: 0 }} />
          <Typography variant="caption">
            Lost a key? For security it can&apos;t be viewed again — revoke it
            and create a new one.
          </Typography>
        </Stack>
      )}
    </>
  );

  const renderCreateBody = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Give the key a recognizable name and choose how long it should stay
        valid.
      </Typography>
      <Stack spacing={2.5}>
        <CustomInput
          id={NAME_FIELD_ID}
          label="Name"
          placeHolder="Zapier"
          name="apiKeyName"
          type="text"
          required
          autoFocus
          value={name}
          onChange={(event: any) => {
            setName(event.target.value);
            if (nameError) setNameError("");
          }}
          error={nameError}
          helperText={
            nameError || `${name.trim().length}/${NAME_MAX} characters`
          }
          propsToInputElement={{ maxLength: NAME_MAX }}
          sx={{
            "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
              outline: "2px solid",
              outlineColor: "formField.focusBorder",
              outlineOffset: "2px",
            },
          }}
        />
        <Box>
          <InputLabel
            id="api_key_expiry_label"
            htmlFor="api_key_expiry"
            sx={{ mb: 0.75, fontWeight: 600, color: "text.primary" }}
          >
            Expires in
          </InputLabel>
          <Select
            fullWidth
            size="small"
            id="api_key_expiry"
            labelId="api_key_expiry_label"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(Number(event.target.value))}
            sx={{
              "& .MuiSelect-select:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "-2px",
              },
            }}
          >
            {EXPIRY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Stack>
    </>
  );

  const renderRevealBody = () => (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          p: 1.5,
          mb: 2.5,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.14),
          color: theme.palette.warning.dark,
        }}
      >
        <WarningAmberOutlinedIcon fontSize="small" sx={{ mt: "1px" }} />
        <Typography variant="body2" fontWeight={500}>
          This is the only time you&apos;ll see this key. Copy it now and store
          it somewhere safe — you won&apos;t be able to view it again.
        </Typography>
      </Box>

      <Typography
        variant="body2"
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 0.75 }}
      >
        Your API key
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.neutral[300]}`,
          bgcolor: theme.palette.neutral[50],
        }}
      >
        <Typography
          variant="body2"
          sx={{
            wordBreak: "break-all",
            userSelect: "all",
            flex: 1,
            color: "text.primary",
          }}
        >
          {revealKey?.full_key}
        </Typography>
        <Tooltip title={copied ? "Copied" : "Copy"} arrow>
          <IconButton
            onClick={handleCopy}
            size="small"
            aria-label={copied ? "API key copied" : "Copy API key"}
            sx={{ color: copied ? theme.palette.success.main : "primary.main" }}
          >
            {copied ? (
              <CheckCircleOutlineIcon fontSize="small" />
            ) : (
              <ContentCopyOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Paste this into Zapier&apos;s <b>API Key</b> field when connecting your
        Inkfree account.
      </Typography>
    </>
  );

  return (
    <SettingsLayout
      activeTab="Integrations"
      title={
        <Box>
          <Typography component="h1" sx={styles.pageTitleSx}>
            Integrations
          </Typography>
          <Typography sx={styles.pageSubtitleSx}>
            Connect Inkfree to the tools you already use.
          </Typography>
        </Box>
      }
      /*
        Sits on the title row like the primary action on Roles, Teams and
        Enterprise SSO, rather than inside the card's own header. Dropped
        entirely when the feature is gated — there is nothing to create.
      */
      headerAction={
        featureAvailable ? (
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
            disabled={loading}
            sx={styles.createButtonSx}
          >
            Create API key
          </Button>
        ) : undefined
      }
    >
      <Box>
        <Card variant="outlined" sx={styles.cardSx}>
          {/* Zapier header */}
          <Box sx={styles.sectionHeaderSx}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={styles.sectionGlyphSx}>
                <VpnKeyOutlinedIcon sx={{ color: "primary.main", fontSize: 15 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h2" sx={styles.sectionTitleSx}>
                  API Keys
                </Typography>
                <Typography component="span" sx={styles.sectionDescSx}>
                  Issue a key to connect Inkfree to your tools. You can use the
                  same API key for Zapier.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: theme.palette.neutral[200] }} />

          {/*
              A locked screen used to replace the whole card with a centred
              gate card, which read as a different pattern from every other
              gated setting. The card now stays put and the lock is explained
              in place, as on Delegation and Document Settings.
            */}
          {!featureAvailable && (
            <Box sx={styles.gateBannerSx}>
              <FeatureBanner
                state={featureState}
                featureName="API access"
                variant="compact"
              />
            </Box>
          )}

          {/*
              No body padding here — same as the Invoices card: the column
              header and every row manage their own inset (`space.gutter`) so
              the header strip and row dividers can run edge to edge.
            */}
          <Box aria-busy={loading}>{renderContent()}</Box>
        </Card>
        {/* Embedded Components: open to every account that can use API keys. */}
        {featureAvailable && <EmbedOrigins announce={announce} />}
      </Box>

      {/* Create → one-time reveal */}
      {/*
        One shell for both steps: creating the key and then revealing it once.
        The reveal step is the only chance to copy the secret, so Esc is locked
        there — the same guard the old dialog carried.
      */}
      <AppModal
        open={createOpen}
        onClose={handleDialogClose}
        title={revealKey ? "API key created" : "Create API key"}
        tone={revealKey ? "success" : "default"}
        size="md"
        hideClose={!!revealKey}
        disableBackdropClose
        disableEscapeClose={!!revealKey}
        busy={creating}
        primaryLabel={
          revealKey ? "I've saved it" : creating ? "Creating…" : "Create key"
        }
        onPrimary={revealKey ? handleRevealDone : handleCreate}
        primaryLoading={creating}
        cancelLabel={revealKey ? undefined : "Cancel"}
        onCancel={() => setCreateOpen(false)}
      >
        {revealKey ? renderRevealBody() : renderCreateBody()}
      </AppModal>

      {/* Revoke confirmation */}
      <AppModal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title={`Revoke "${revokeTarget?.name}"?`}
        tone="destructive"
        size="sm"
        busy={revoking}
        primaryLabel={revoking ? "Revoking…" : "Revoke key"}
        onPrimary={handleRevoke}
        primaryLoading={revoking}
        cancelLabel="Cancel"
        onCancel={() => setRevokeTarget(null)}
      >
        <Typography sx={bodyTextSx}>
          Any Zap or integration using this key will stop working immediately.
          This cannot be undone.
        </Typography>
      </AppModal>

      <LiveRegion message={announcement} priority={priority} />
    </SettingsLayout>
  );
};

export default ApiKeys;
